export interface CityData {
  slug: string;
  name: string;
  state: "IN" | "MI";
  stateName: "Indiana" | "Michigan";
  distance: number;
  direction: string;
  highways: string;
  zips: string;
  county: string;
  intro: string;
  localContext: string;
  whyChoose: [string, string, string, string];
  midContent: string;
  faqs: [{ q: string; a: string }, { q: string; a: string }];
  neighborhoods: string;
}

export const CITIES: CityData[] = [
  {
    slug: "granger-in",
    name: "Granger",
    state: "IN",
    stateName: "Indiana",
    distance: 18,
    direction: "northwest",
    highways: "US-20, SR-23, and the Indiana Toll Road",
    zips: "46530",
    county: "St. Joseph County",
    intro:
      "Granger sits about 18 miles northwest of our Elkhart shop, an easy run up SR-23 or the Toll Road. We see a steady flow of Granger customers - boat owners headed to the Michigan lakes, contractors with utility trailers, and families towing campers up north for the weekend.",
    localContext:
      "Granger is heavy on recreational towing. Pontoons and ski boats bound for Diamond Lake, Eagle Lake, and the Michigan inland chain come through our service bay every spring for bearing repacks and brake checks. Cargo trailers servicing the Heritage Square and Toscana Park commercial corridors are the second-biggest segment we see from the area.",
    whyChoose: [
      "Same-day bearing service so your boat trailer is ready before the weekend launch at Lake Michiana or Diamond Lake.",
      "Direct line to Dexter Group OEM parts - capacity ratings up to 10,000 lb, in-stock for most Granger-area trailers.",
      "Easy 25-minute drive down US-20 or SR-23, with parts pickup or full service in the same trip.",
      "Local relationships with Granger boat dealers and RV service shops - we know the equipment that lives here.",
    ],
    midContent:
      "If you tow out of Granger regularly, the single biggest thing you can do for trailer life is yearly bearing service. The water dunks at Diamond Lake and the salt residue from winter storage on US-23 both shorten bearing life faster than highway miles do. Bring it down to Elkhart in April or May and we'll have you ready for the season in about an hour.",
    faqs: [
      {
        q: "Do you serve trailer customers from Granger, IN?",
        a: "Yes - Granger is one of our busiest service areas outside Elkhart proper. Most Granger customers schedule online at axle.setmore.com and drive down via SR-23 or US-20. The trip is about 25 minutes each way and parts are almost always in stock when you arrive.",
      },
      {
        q: "What's the closest trailer axle shop to Granger?",
        a: "Axle Inc. in Elkhart is the closest dedicated trailer-axle shop and the only authorized Dexter Group distributor serving the Granger area. We stock axles, hubs, brakes, and suspension parts up to 10,000 lb capacity for boat, utility, cargo, and small enclosed trailers.",
      },
    ],
    neighborhoods: "Heritage Square, Toscana Park, Adams Pointe, and the SR-23 corridor",
  },
  {
    slug: "mishawaka-in",
    name: "Mishawaka",
    state: "IN",
    stateName: "Indiana",
    distance: 14,
    direction: "west",
    highways: "US-20, US-33, and SR-933",
    zips: "46544, 46545",
    county: "St. Joseph County",
    intro:
      "Mishawaka is roughly 14 miles west of our Elkhart shop along US-20 - a 20-minute run for most customers. The St. Joseph River corridor through Mishawaka means we see a lot of pontoon and fishing-boat trailers, plus a healthy share of contractor and landscape trailers from the Capital Avenue and Grape Road commercial belt.",
    localContext:
      "Mishawaka has a deep manufacturing and trades footprint. Service-truck owners hauling tools, equipment trailers running between job sites, and fleet operators keeping cargo trailers on the road are routine customers for us. Boat trailers from the Princess Boat Cruises area and the river launches are the recreational backbone.",
    whyChoose: [
      "Quick 20-minute hop down US-20 - the closest full-line trailer parts shop to most of Mishawaka.",
      "Walk-in parts counter for hubs, bearings, lights, couplers, and jacks - no appointment needed for parts.",
      "Authorized Dexter dealer with current rebates - significant savings on full axle replacements.",
      "Service bay capable of same-day bearing repacks, brake jobs, and spindle replacements on light-duty trailers.",
    ],
    midContent:
      "We see the same Mishawaka contractors year after year because we keep their trailers on the road. A landscape company that loses a trailer for a week loses money fast - that's why we keep light-duty axles, brakes, and hubs in stock and prioritize fleet turnaround. Set up a fleet account and we'll keep your maintenance schedule documented.",
    faqs: [
      {
        q: "How long does it take to drive from Mishawaka to Axle Inc.?",
        a: "About 20 minutes via US-20 east. Most Mishawaka customers drop a trailer in the morning and pick it up the same afternoon for routine bearing or brake work.",
      },
      {
        q: "Do you supply parts to Mishawaka contractors and fleets?",
        a: "Yes. We service local landscape, plumbing, and contractor fleets out of the Mishawaka and South Bend area. Set up a fleet account by calling the shop, and we'll keep priority stock for your common parts.",
      },
    ],
    neighborhoods: "Capital Avenue, Grape Road, Town & Country, and the SR-933 corridor",
  },
  {
    slug: "goshen-in",
    name: "Goshen",
    state: "IN",
    stateName: "Indiana",
    distance: 11,
    direction: "southeast",
    highways: "US-33, SR-15, and CR-17",
    zips: "46526, 46528",
    county: "Elkhart County",
    intro:
      "Goshen is our closest neighbor - about 11 miles southeast on US-33, less than 20 minutes door to door. Many of our long-time customers live and work in Goshen, and we run parts to Goshen-based RV plants and manufacturers on a regular basis.",
    localContext:
      "Goshen is the southern anchor of the RV manufacturing corridor. We see custom builds for cargo and toy haulers, fleet maintenance for delivery trailers, and a heavy stream of farm trailers from the rural areas south and east of town. Goshen College and downtown traffic also bring in the smaller utility and bike-rack trailers.",
    whyChoose: [
      "Same-county service - fastest response and pickup for any Goshen address.",
      "Direct stock support for Goshen RV manufacturers and component suppliers needing axles or running gear.",
      "Custom axle builds spec'd to your exact length, capacity, and hub - built and ready in days, not weeks.",
      "Walk-in parts counter open for Goshen contractors, farmers, and weekend warriors alike.",
    ],
    midContent:
      "Goshen's farm and RV economy means we see more custom builds out of this town than anywhere else. If you need a non-standard length, an unusual hub face, or a brake type that nobody else stocks, this is where the request usually originates. Bring us your spec sheet or your old axle and we'll match it - most builds turn around in 3-7 business days.",
    faqs: [
      {
        q: "Do you build custom trailer axles for Goshen RV manufacturers?",
        a: "Yes. Goshen is our backyard and we work directly with several RV-industry component buyers and small manufacturers. Custom Dexter axles, hub kits, and brake assemblies - spec'd to your drawing and built to order.",
      },
      {
        q: "Where can I buy a trailer axle in Goshen, IN?",
        a: "Axle Inc. in Elkhart is the closest authorized Dexter distributor - about 11 miles up US-33. We carry axles from 1,000 lb up to 10,000 lb capacity, in idler or brake configurations, with hubs and running gear in stock.",
      },
    ],
    neighborhoods: "downtown Goshen, College Avenue, Lincolnway, and the CR-17 corridor",
  },
  {
    slug: "south-bend-in",
    name: "South Bend",
    state: "IN",
    stateName: "Indiana",
    distance: 22,
    direction: "west",
    highways: "US-20, US-31, and the Indiana Toll Road",
    zips: "46601, 46613, 46614, 46615, 46616, 46617, 46619, 46628, 46635, 46637",
    county: "St. Joseph County",
    intro:
      "South Bend is about 22 miles west of Elkhart, an easy 30-minute run on US-20 or the Toll Road. We've been serving South Bend trailer customers for decades - fleets, weekend boaters, Notre Dame area contractors, and the manufacturing community along the West Side.",
    localContext:
      "South Bend trailer work splits roughly evenly between commercial fleets, RV/boat owners headed to the Michigan lakes, and the Notre Dame-area contractors keeping the campus and city projects moving. We also see a steady stream of car-hauler and equipment trailers from the south-side industrial parks.",
    whyChoose: [
      "Fast 30-minute drive on US-20 - closer than any other authorized Dexter dealer to most of South Bend.",
      "Volume parts pricing for South Bend fleets, manufacturers, and dealers - call to set up an account.",
      "Same-day bearing, brake, and spindle service - drop in the morning, drive home the same afternoon.",
      "Sixty-plus years of combined experience servicing the South Bend / Mishawaka / Elkhart trailer market.",
    ],
    midContent:
      "South Bend customers tend to under-service their trailers because daily-driver mileage hides the wear. A trailer that sees 2,500 miles a year still gets the same UV, salt, and water exposure as a car that sees 12,000. Yearly bearing inspection and brake checks are the two easiest ways to avoid a roadside breakdown on the Toll Road or out on US-31 north.",
    faqs: [
      {
        q: "Where's the best place to buy trailer axles near South Bend, IN?",
        a: "Axle Inc. in Elkhart, about 22 miles east on US-20. We're the area's authorized Dexter Group distributor and stock axles from 1,000 lb up to 10,000 lb capacity, plus complete running-gear kits for boat, utility, cargo, and enclosed trailers.",
      },
      {
        q: "Do you handle commercial trailer accounts in South Bend?",
        a: "Yes - we work with fleets, dealers, and manufacturers across the South Bend / Mishawaka / Elkhart triangle. Volume pricing, dedicated stock for your common parts, and priority service-bay slots when one of your trailers is down.",
      },
    ],
    neighborhoods: "Northside, West Side, the Notre Dame area, and Eddy Street Commons",
  },
  {
    slug: "bristol-in",
    name: "Bristol",
    state: "IN",
    stateName: "Indiana",
    distance: 8,
    direction: "northeast",
    highways: "SR-15 and CR-131",
    zips: "46507",
    county: "Elkhart County",
    intro:
      "Bristol is just 8 miles northeast of our Elkhart shop along SR-15 - under 15 minutes for most customers. The St. Joseph River frontage and Bonneyville Mill area make boat-trailer service the dominant request from Bristol residents.",
    localContext:
      "Bristol is small-town Indiana with big-water access. The river launches and the proximity to the Michigan lakes mean we see fishing boats, pontoons, and ski-boat trailers all spring and summer. The rural area north and east of town also brings us a steady mix of horse trailers, farm utility trailers, and the occasional gooseneck.",
    whyChoose: [
      "Closest authorized Dexter dealer - Bristol residents are at our shop in 15 minutes flat.",
      "Boat-trailer specialty: marine-grade bearings, sealed hubs, and corrosion-resistant brake hardware in stock.",
      "Walk-in parts counter open for the same-day fix when you discover a wobble in the driveway Saturday morning.",
      "Honest assessment first - we'll tell you what needs replacing and what's still good for another season.",
    ],
    midContent:
      "Boat trailers out of Bristol live a hard life - submerged hubs at every launch, road salt on the way home, and long winter sits. We recommend yearly bearing service, period. Skip a year and you'll likely lose a bearing on a Friday afternoon halfway to the lake. The repack costs less than a single new hub.",
    faqs: [
      {
        q: "Do you service boat trailers from Bristol, IN?",
        a: "Yes - boat-trailer service is one of the most common requests we get from Bristol. Marine-grade bearing repacks, sealed hub installations, and brake service for trailers that get dunked. Schedule online or call to drop one off.",
      },
      {
        q: "How far is Axle Inc. from Bristol, IN?",
        a: "About 8 miles down SR-15 - usually 12-15 minutes depending on traffic. Most Bristol customers drop a trailer and pick it up the same day for routine service.",
      },
    ],
    neighborhoods: "downtown Bristol, the Bonneyville Mill area, and the SR-15 corridor",
  },
  {
    slug: "middlebury-in",
    name: "Middlebury",
    state: "IN",
    stateName: "Indiana",
    distance: 12,
    direction: "east",
    highways: "US-20 and SR-13",
    zips: "46540",
    county: "Elkhart County",
    intro:
      "Middlebury is about 12 miles east of Elkhart on US-20 - heart of Indiana Amish country and home to several major RV manufacturers. We work with Middlebury trailer customers ranging from horse-trailer owners to commercial RV fleets.",
    localContext:
      "Middlebury trailer work is a unique mix. Horse and livestock trailers from the surrounding Amish farms come in for spring suspension and brake service. The town's RV manufacturers - Jayco, Heartland, and others nearby - drive a steady stream of axle and component requests. And the Das Dutchman Essenhaus tourist traffic adds the smaller utility and tow-behind trailers.",
    whyChoose: [
      "RV-industry-grade parts: tandem axles, electric brakes, and torsion suspensions in stock for Middlebury manufacturers.",
      "Horse and livestock trailer specialists - heavier-duty bearings, brakes, and floor-related running gear.",
      "Direct shot east on US-20 - under 20 minutes from most Middlebury addresses.",
      "Custom axle builds for the unusual configurations Middlebury manufacturers and farmers actually need.",
    ],
    midContent:
      "Horse trailer owners in Middlebury, this is the maintenance reminder you needed: torsion-axle bearings have to be re-greased through the zerk fittings yearly, and the brake magnets wear out faster on heavily-loaded livestock trailers than on cargo trailers of the same weight. We see both failures every spring. Bring it in once a year and you'll never be stranded with horses on board.",
    faqs: [
      {
        q: "Do you work on horse and livestock trailers from Middlebury?",
        a: "Yes. Horse trailers are heavier and more sensitive to bearing or brake failure than most light-duty trailers - we service their running gear regularly for Middlebury and northern Elkhart County customers.",
      },
      {
        q: "Do you supply axles to Middlebury RV manufacturers?",
        a: "Yes. We work with several Middlebury and Goshen RV manufacturers and suppliers on volume axle, brake, and suspension orders. Authorized Dexter distributor with current OEM pricing and rebates.",
      },
    ],
    neighborhoods: "downtown Middlebury, the Das Dutchman Essenhaus area, and the US-20 corridor",
  },
  {
    slug: "nappanee-in",
    name: "Nappanee",
    state: "IN",
    stateName: "Indiana",
    distance: 19,
    direction: "south",
    highways: "US-6 and SR-19",
    zips: "46550",
    county: "Elkhart County",
    intro:
      "Nappanee is about 19 miles south of Elkhart down SR-19 - 25 to 30 minutes door to door. Like Middlebury, Nappanee is RV manufacturing country (Newmar is headquartered here) and surrounded by Amish farmland that drives a steady demand for working trailers.",
    localContext:
      "Nappanee customers run the full range. Newmar and the cluster of small RV manufacturers drive component-level orders for axles, brakes, and suspension. The surrounding Amish farms generate steady demand for horse, livestock, and farm utility trailer service. And the Amish Acres / downtown Nappanee tourism draws the small-trailer crowd.",
    whyChoose: [
      "Component-level supplier to Nappanee RV manufacturing - axles, brakes, hubs, and suspension in volume.",
      "Farm and livestock trailer experience - we know how to spec for the loads Amish-country trailers actually carry.",
      "Easy SR-19 drive north - 25 minutes for most Nappanee customers, with same-day service typical.",
      "Custom axle builds for unusual lengths and brake configurations common in Nappanee-built trailers.",
    ],
    midContent:
      "If you've recently bought a Nappanee-built RV or trailer and you're not sure what brand of axle or brake assembly is underneath, photograph the spec plate and bring or email it to us. We can identify the components, source replacements, and get you back on the road. Most Nappanee-area trailers we see are running Dexter or Lippert running gear - both of which we stock.",
    faqs: [
      {
        q: "Do you service trailers built by Nappanee RV manufacturers?",
        a: "Yes - we regularly service trailers and RVs built in Nappanee, Goshen, and Middlebury. The running gear is almost always Dexter or Lippert, both of which we stock and support.",
      },
      {
        q: "How far is Axle Inc. from Nappanee, IN?",
        a: "About 19 miles north up SR-19 - typically a 25 to 30 minute drive. Schedule online at axle.setmore.com or call ahead and we'll have parts ready when you arrive.",
      },
    ],
    neighborhoods: "downtown Nappanee, the Amish Acres area, and the SR-19 corridor",
  },
  {
    slug: "wakarusa-in",
    name: "Wakarusa",
    state: "IN",
    stateName: "Indiana",
    distance: 13,
    direction: "south",
    highways: "SR-19 and CR-46",
    zips: "46573",
    county: "Elkhart County",
    intro:
      "Wakarusa is roughly 13 miles south of Elkhart down SR-19 - under 20 minutes for most customers. Small town, deep agricultural roots, and a steady customer base of farmers and tradesmen who depend on their trailers running every day.",
    localContext:
      "Wakarusa trailer work is overwhelmingly utility and farm-focused. Equipment haulers, livestock trailers, and contractor utility trailers make up the bulk of what we see from Wakarusa addresses. Coppes Commons and the downtown traffic occasionally bring in the smaller bike-rack and folding-trailer crowd.",
    whyChoose: [
      "Direct SR-19 corridor - fastest run from Wakarusa to a full-line trailer parts shop in northern Indiana.",
      "Farm and equipment trailer specialty - we know the loads, the bearings, and the brakes that hold up.",
      "Walk-in parts counter for the Saturday morning emergency repair - hubs, bearings, brake parts, lights, couplers.",
      "Honest pricing - fair quotes, no upsell on parts you don't need.",
    ],
    midContent:
      "If your Wakarusa trailer hauls equipment 5+ days a week, treat it like any other piece of farm machinery: yearly bearing service, brake inspection every spring, and check the suspension hangers and shackles for ovaling. The hangers are the silent killer on hard-used utility trailers - once the hole elongates, the spring shifts, the geometry goes off, and tire wear gets ugly fast.",
    faqs: [
      {
        q: "Do you service farm and equipment trailers from Wakarusa, IN?",
        a: "Yes - heavy-duty farm and equipment trailers are a regular part of our work. We service bearings, brakes, suspension, and full axle replacements up to 10,000 lb capacity.",
      },
      {
        q: "How long does it take to drive from Wakarusa to Axle Inc.?",
        a: "About 18-20 minutes up SR-19. Most Wakarusa customers drop a trailer in the morning and pick it up the same day for routine service.",
      },
    ],
    neighborhoods: "downtown Wakarusa, Coppes Commons, and the SR-19 corridor",
  },
  {
    slug: "new-paris-in",
    name: "New Paris",
    state: "IN",
    stateName: "Indiana",
    distance: 16,
    direction: "south",
    highways: "SR-15 and CR-46",
    zips: "46553",
    county: "Elkhart County",
    intro:
      "New Paris is a small farming community about 16 miles south of Elkhart, just south of Goshen on SR-15. Quiet town, working trailers, and customers who appreciate straight answers - that describes most of what we see from New Paris addresses.",
    localContext:
      "New Paris is rural through and through. Farm trailers, equipment haulers, and livestock trailers are nearly everything we service from this area. The town's Amish and Mennonite community also brings in horse and buggy-related trailer hardware - couplers, lights, and small utility running gear.",
    whyChoose: [
      "Closest dedicated trailer parts shop to New Paris - about 20 minutes up SR-15 through Goshen.",
      "Farm-tested bearing and brake parts in stock - the same components that have served the area for sixty years combined.",
      "Walk-in welcome - no appointment for parts, schedule online for service.",
      "Honest assessment - we'll fix what needs fixing and tell you what doesn't.",
    ],
    midContent:
      "New Paris farm trailers see hard, irregular loads. The single most common failure we see from this area is bearing failure from missed service intervals - a trailer used heavily for three weeks of harvest, parked for months, and pulled out again next season without inspection. Yearly bearing service prevents almost every roadside failure we see.",
    faqs: [
      {
        q: "Where can I get trailer parts near New Paris, IN?",
        a: "Axle Inc. in Elkhart, about 16 miles north on SR-15 through Goshen. We're the closest authorized Dexter distributor and carry the full line of axles, hubs, brakes, and suspension parts up to 10,000 lb capacity.",
      },
      {
        q: "Do you service horse and buggy hardware?",
        a: "We don't service buggies themselves, but we do supply small-trailer running gear, couplers, lights, and hardware commonly used by Amish and Mennonite customers in New Paris and the surrounding area.",
      },
    ],
    neighborhoods: "downtown New Paris and the SR-15 corridor",
  },
  {
    slug: "plymouth-in",
    name: "Plymouth",
    state: "IN",
    stateName: "Indiana",
    distance: 32,
    direction: "south",
    highways: "US-30 and US-31",
    zips: "46563",
    county: "Marshall County",
    intro:
      "Plymouth is about 32 miles south of Elkhart down US-31 - 40 minutes door to door. Marshall County seat, home of the Blueberry Festival, and a deep base of farmers, contractors, and recreational towers who count on their trailers.",
    localContext:
      "Plymouth trailer customers are mostly farm and contractor. The agricultural belt around Plymouth means a lot of livestock and equipment trailers in our shop every spring. The Lake Maxinkuckee crowd in nearby Culver also drives boat-trailer service from this area, and downtown Plymouth contractors round out the rest.",
    whyChoose: [
      "Worth the drive: northern Indiana's most complete trailer parts and service shop, 40 minutes up US-31.",
      "Authorized Dexter distributor - current OEM pricing and rebates not available at smaller suppliers.",
      "Custom axle builds - spec'd to fit, including unusual lengths needed by Plymouth-area farm equipment trailers.",
      "Same-day service common for routine bearing, brake, and hub work - drop in the morning, drive home the same day.",
    ],
    midContent:
      "Plymouth customers tend to batch their service work - bring two or three trailers up at once for a once-a-year bearing-and-brake inspection across the whole fleet. We accommodate that gladly. Call ahead so we can stage parts and reserve service-bay time, and you'll be in and out in a single trip with multiple trailers serviced.",
    faqs: [
      {
        q: "Is it worth driving from Plymouth to Elkhart for trailer service?",
        a: "For routine bearing service or brake parts, often yes - Axle Inc. carries inventory and capacity that smaller local shops don't. Customers who batch multiple trailers per visit get the most value from the trip.",
      },
      {
        q: "Do you stock axles for Plymouth farm equipment trailers?",
        a: "Yes. We carry capacity ratings from 1,000 lb up to 10,000 lb, with brake or idler configurations and the hubs/running gear to match. Custom builds available for unusual lengths.",
      },
    ],
    neighborhoods: "downtown Plymouth, the Lake Maxinkuckee corridor, and the US-30 / US-31 junction",
  },
  {
    slug: "warsaw-in",
    name: "Warsaw",
    state: "IN",
    stateName: "Indiana",
    distance: 38,
    direction: "southeast",
    highways: "US-30 and SR-15",
    zips: "46580, 46582",
    county: "Kosciusko County",
    intro:
      "Warsaw is about 38 miles southeast of Elkhart down SR-15 - 45 to 50 minutes door to door. Orthopedic-industry hub, home to Lake Wawasee and Lake Tippecanoe, and one of the most active recreational-trailer areas in our service region.",
    localContext:
      "Warsaw is boat trailer country. Lake Wawasee, Lake Tippecanoe, and Pike Lake mean every spring brings a flood of trailers in for bearing and brake service after winter storage. The orthopedic and biomedical industry in Warsaw also drives commercial trailer work - equipment movers, supply trailers, and fleet maintenance.",
    whyChoose: [
      "Largest in-stock selection of marine-grade bearings, sealed hubs, and corrosion-resistant brake hardware in northern Indiana.",
      "Same-day service for boat-trailer bearing repacks - drop Tuesday, launch Saturday on Wawasee.",
      "Authorized Dexter distributor with capacity for full axle replacements when a tube has been bent or fatigued.",
      "Volume parts pricing for Warsaw-area medical and biomedical industry fleets.",
    ],
    midContent:
      "Lake Wawasee and Lake Tippecanoe boat trailers see some of the harshest service in our region - repeated dunks, long highway runs, and the occasional sit-with-water-in-the-hub winter. We tell every Warsaw boat-trailer owner the same thing: marine-grade grease, sealed hubs if your trailer is old enough to warrant the upgrade, and yearly inspection without exception. Skip the inspection and the failure usually happens at the worst possible moment.",
    faqs: [
      {
        q: "Do you service boat trailers for Lake Wawasee and Lake Tippecanoe?",
        a: "Yes - boat trailer service from the Wawasee, Tippecanoe, and Pike Lake areas is a steady part of our spring and fall workload. Marine-grade bearing repacks, sealed-hub upgrades, and brake service. Schedule online or call ahead.",
      },
      {
        q: "Where's the closest authorized Dexter dealer to Warsaw, IN?",
        a: "Axle Inc. in Elkhart is the closest authorized Dexter Group distributor - about 38 miles up SR-15. We stock the full Dexter axle, brake, and suspension line up to 10,000 lb capacity.",
      },
    ],
    neighborhoods: "downtown Warsaw, Lake Wawasee, Lake Tippecanoe, and the US-30 corridor",
  },
  {
    slug: "osceola-in",
    name: "Osceola",
    state: "IN",
    stateName: "Indiana",
    distance: 9,
    direction: "west",
    highways: "US-33 and SR-933",
    zips: "46561",
    county: "St. Joseph County",
    intro:
      "Osceola sits between Elkhart and Mishawaka on US-33 - about 9 miles from our shop, under 15 minutes for most customers. The St. Joseph County / Elkhart County border community has a healthy mix of recreational and working trailer owners.",
    localContext:
      "Osceola sits right on the river and on a major commuter and recreational corridor. We see boat trailers headed to area lakes, contractor and landscape trailers from the Cleveland Road / SR-933 commercial belt, and a steady share of the smaller utility trailers that come with suburban homeownership.",
    whyChoose: [
      "Closest dedicated trailer parts shop - straight up US-33, under 15 minutes from most Osceola addresses.",
      "Walk-in counter for parts, online scheduling for service - easiest workflow in the area.",
      "Authorized Dexter dealer with full axle and running-gear stock for trailers up to 10,000 lb.",
      "Service bay for same-day bearing repacks, brake jobs, and hub replacements on most light-duty trailers.",
    ],
    midContent:
      "Osceola is close enough that we get a lot of last-minute customers - the Friday afternoon discovery of a wobble, the Saturday morning emergency before the boat launch, the Sunday evening realization that the camper is leaving Monday. Walk in or call ahead and we'll do what we can same-day. The proximity matters here.",
    faqs: [
      {
        q: "Can I drop in for trailer parts in Osceola, IN?",
        a: "We're 9 miles east on US-33 - about 12 to 15 minutes from most Osceola addresses. Walk-in parts counter is open during business hours; service work is scheduled at axle.setmore.com or by phone.",
      },
      {
        q: "Do you handle emergency trailer repairs from Osceola?",
        a: "When the schedule allows, yes. Call the shop and tell us what's wrong. We'll triage and let you know whether we can fit you in same-day. Walk-ins for parts are always welcome.",
      },
    ],
    neighborhoods: "Cleveland Road, the SR-933 corridor, and the river-adjacent residential areas",
  },
  {
    slug: "dunlap-in",
    name: "Dunlap",
    state: "IN",
    stateName: "Indiana",
    distance: 5,
    direction: "south",
    highways: "US-33 and CR-17",
    zips: "46517",
    county: "Elkhart County",
    intro:
      "Dunlap is essentially next door - about 5 miles south of our Elkhart shop on US-33, under 10 minutes for most customers. The Concord schools area community is one of our closest service zones and a major source of regular maintenance customers.",
    localContext:
      "Dunlap is residential and family-oriented, and the trailer mix reflects that. Boat trailers, small enclosed cargo trailers, motorcycle and ATV trailers, and a steady stream of utility trailers are typical. The convenience of our location means many Dunlap customers walk in for parts and schedule service the same week.",
    whyChoose: [
      "Practically a neighbor - under 10 minutes from any Dunlap address. The closest authorized Dexter dealer, period.",
      "Walk-in parts for the Saturday morning fix - hubs, bearings, lights, couplers, jacks, all in stock.",
      "Same-day service common for routine work - bearing repacks, brake jobs, hub swaps.",
      "Family-run shop with sixty-plus combined years of experience - the same people behind the counter every visit.",
    ],
    midContent:
      "Dunlap residents sometimes assume the local big-box auto-parts store is the right place for trailer parts. It's not. Trailer hubs, bearings, and brakes are spec'd differently from automotive equivalents, and getting the wrong part costs you a return trip. Drop into our shop instead - same drive time, right parts the first time.",
    faqs: [
      {
        q: "How close is Axle Inc. to Dunlap, IN?",
        a: "About 5 miles north on US-33 - under 10 minutes for most Dunlap residents. We're often closer than the nearest big-box auto-parts store and we actually stock the trailer-specific parts you need.",
      },
      {
        q: "Do you sell trailer parts for walk-in customers?",
        a: "Yes - walk-in parts counter is open during business hours. Hubs, bearings, brake parts, lights, couplers, jacks, and full axles in stock. Bring your old part or take a photo of the spec plate.",
      },
    ],
    neighborhoods: "Concord schools area, the US-33 corridor, and CR-17",
  },
  {
    slug: "millersburg-in",
    name: "Millersburg",
    state: "IN",
    stateName: "Indiana",
    distance: 17,
    direction: "southeast",
    highways: "US-20 and SR-13",
    zips: "46543",
    county: "Elkhart County",
    intro:
      "Millersburg is about 17 miles southeast of Elkhart along US-20 - 25 minutes for most customers. Small town, deep Amish-country roots, and a customer base of farmers, tradespeople, and rural homeowners who put real miles on real trailers.",
    localContext:
      "Millersburg trailer work is heavily farm and livestock-oriented, with a meaningful share of horse trailers from the surrounding LaGrange and Elkhart County Amish communities. We also see contractor and equipment trailers from the rural construction and woodworking trades that thrive in this area.",
    whyChoose: [
      "Direct US-20 run east - under 25 minutes from most Millersburg addresses to our service bay.",
      "Heavy-duty farm and livestock trailer experience - bearings and brakes spec'd for actual loaded weight, not factory minimums.",
      "Walk-in parts counter for the emergency Saturday morning repair, with most common parts in stock.",
      "Custom axle builds for the unusual configurations that show up regularly on rural Millersburg-area trailers.",
    ],
    midContent:
      "If you haul livestock out of Millersburg, the math on yearly bearing and brake service is simple: a $150 inspection prevents a $1,500 roadside breakdown with animals on board, plus the cost of a replacement tow vehicle and the lost time. We see one or two of those failures every year and they are all preventable. Bring it in once a year, period.",
    faqs: [
      {
        q: "Do you service horse and livestock trailers from Millersburg?",
        a: "Yes - these are some of our most regular customers. Horse and livestock trailers are heavier than most light-duty trailers and need more frequent bearing, brake, and suspension service. Schedule online or call to drop one off.",
      },
      {
        q: "How far is Millersburg from Axle Inc.?",
        a: "About 17 miles west on US-20 - typically 22 to 25 minutes depending on traffic. Most Millersburg customers drop a trailer in the morning and pick it up the same afternoon.",
      },
    ],
    neighborhoods: "downtown Millersburg and the US-20 corridor toward Topeka",
  },
  {
    slug: "kendallville-in",
    name: "Kendallville",
    state: "IN",
    stateName: "Indiana",
    distance: 48,
    direction: "east",
    highways: "US-6 and SR-3",
    zips: "46755",
    county: "Noble County",
    intro:
      "Kendallville is about 48 miles east of Elkhart - 60 to 70 minutes door to door depending on the route. Noble County's largest community, home to Bixler Lake, and a regular source of customers who make the trip for service that justifies the drive.",
    localContext:
      "Kendallville customers split between recreational boaters using Bixler Lake and the surrounding Noble County waterways, and the agricultural and contractor base that defines small-town northeast Indiana. We see boat, utility, equipment, and the occasional horse trailer from this area.",
    whyChoose: [
      "Worth the drive: the most complete trailer parts and Dexter axle inventory in northern Indiana.",
      "Authorized Dexter distributor - current OEM pricing and rebates not available at most local suppliers.",
      "Custom axle builds and same-day service for routine bearing and brake work - call ahead and batch your visit.",
      "Sixty-plus combined years of experience - when you make the drive, you get expertise you can't find closer.",
    ],
    midContent:
      "Customers who drive to us from Kendallville tend to do the smart thing: combine the trip. Bring two or three trailers up at once, or pick up parts for the next two services along with the work that brought you in. We'll stage parts ahead of time if you call before the visit, and most jobs are turn-arounds the same day if scheduled in the morning.",
    faqs: [
      {
        q: "Is it worth driving from Kendallville to Axle Inc. for trailer service?",
        a: "For full axle replacement, custom builds, or major bearing and brake work, often yes - our inventory and expertise are difficult to match closer to Kendallville. Customers who batch work across multiple trailers per visit get the best return on the drive.",
      },
      {
        q: "Do you service Bixler Lake boat trailers from Kendallville?",
        a: "Yes - boat-trailer service from the Bixler Lake and Noble County area is a regular part of our spring workload. Marine-grade bearing repacks and sealed-hub upgrades available.",
      },
    ],
    neighborhoods: "downtown Kendallville, Bixler Lake, and the US-6 corridor",
  },
  {
    slug: "niles-mi",
    name: "Niles",
    state: "MI",
    stateName: "Michigan",
    distance: 28,
    direction: "northwest",
    highways: "US-31, US-12, and M-51",
    zips: "49120",
    county: "Berrien County",
    intro:
      "Niles is about 28 miles northwest of Elkhart, just over the Michigan line on US-31 - 35 to 40 minutes door to door. The St. Joseph River runs through Niles and feeds a steady stream of fishing-boat and pontoon trailer service every season.",
    localContext:
      "Niles customers are heavy on river and lake recreation. Boat trailers headed to the St. Joseph River, Pucker Street Dam launches, and the Lake Michigan beaches are constant. The town's industrial base also drives commercial trailer work - equipment haulers, fleet utility trailers, and small enclosed cargo.",
    whyChoose: [
      "Closest authorized Dexter dealer to Niles - about 35 minutes south on US-31.",
      "Marine-grade bearings, sealed hubs, and corrosion-resistant brake parts in stock for river and lake trailers.",
      "Volume pricing and account setup available for Niles-area commercial fleets and dealers.",
      "Sixty-plus years of combined experience servicing the Michigan-Indiana border region.",
    ],
    midContent:
      "Cross-border customers from Niles get the same service and pricing as our Indiana customers - there's no markup for being out of state, and the drive on US-31 is usually faster than fighting traffic to a comparable shop in southwest Michigan. Schedule online and we'll stage parts before you arrive.",
    faqs: [
      {
        q: "Do you service trailers from Niles, MI?",
        a: "Yes. Niles is about 35 minutes south of our Elkhart shop on US-31. Cross-border service is routine - most Niles customers drop a trailer in the morning and pick up the same afternoon.",
      },
      {
        q: "Where's the closest Dexter axle dealer to Niles, MI?",
        a: "Axle Inc. in Elkhart, IN - the closest authorized Dexter Group distributor to most of southwest Michigan. We carry the full line up to 10,000 lb capacity.",
      },
    ],
    neighborhoods: "downtown Niles, the St. Joseph River corridor, and the US-31 / M-51 junction",
  },
  {
    slug: "sturgis-mi",
    name: "Sturgis",
    state: "MI",
    stateName: "Michigan",
    distance: 30,
    direction: "north",
    highways: "US-12 and M-66",
    zips: "49091",
    county: "St. Joseph County",
    intro:
      "Sturgis is about 30 miles north of Elkhart up SR-15 / M-66 - 40 to 45 minutes door to door. Manufacturing town, home to a strong industrial base, and surrounded by the southern Michigan inland-lake belt that drives recreational trailer work.",
    localContext:
      "Sturgis trailer customers split between the manufacturing fleets that move parts and equipment in and out of town, and the recreational base headed to Klinger Lake, Long Lake, and the inland chain. Both groups get service from us routinely - fleets via account programs, individuals via online scheduling and walk-in parts.",
    whyChoose: [
      "Closest full-line trailer axle and parts shop to Sturgis - about 40 minutes south through Bristol or White Pigeon.",
      "Fleet account programs for Sturgis-area manufacturers needing reliable trailer maintenance and parts supply.",
      "Marine-grade running gear in stock for Klinger Lake and Long Lake boat trailers.",
      "Authorized Dexter dealer - OEM pricing and current rebates available across the Michigan border.",
    ],
    midContent:
      "Sturgis manufacturing customers benefit most from setting up a fleet account. We pre-stock your common parts, prioritize your service-bay slots when a trailer goes down, and bill monthly so your AP team isn't dealing with one-off invoices. Call the shop to set it up - usually a 10-minute conversation.",
    faqs: [
      {
        q: "Do you serve commercial fleets in Sturgis, MI?",
        a: "Yes - we work with manufacturing fleets, dealers, and contractor fleets across the Michigan-Indiana border. Set up an account by calling the shop and we'll keep priority stock for your common parts.",
      },
      {
        q: "How far is Sturgis from Axle Inc.?",
        a: "About 30 miles south - typically 40 to 45 minutes via SR-15 and M-66. Schedule online at axle.setmore.com or call ahead and we'll have parts ready.",
      },
    ],
    neighborhoods: "downtown Sturgis, the M-66 corridor, and the Klinger Lake / Long Lake areas",
  },
  {
    slug: "cassopolis-mi",
    name: "Cassopolis",
    state: "MI",
    stateName: "Michigan",
    distance: 32,
    direction: "northwest",
    highways: "M-60 and M-62",
    zips: "49031",
    county: "Cass County",
    intro:
      "Cassopolis is about 32 miles northwest of Elkhart - 45 minutes door to door. Cass County seat, sitting next to Diamond Lake (Michigan's deepest natural lake), and one of the most boat-trailer-heavy areas we serve.",
    localContext:
      "Diamond Lake drives the trailer economy in Cassopolis. Pontoon, ski-boat, and fishing-boat trailers from Diamond Lake, Stone Lake, and the surrounding inland chain make up the majority of what we see from this area. The agricultural base around the town adds farm and equipment trailers to the mix.",
    whyChoose: [
      "Diamond Lake specialty: marine-grade bearings, sealed hubs, and salt/water-resistant brake hardware always in stock.",
      "Closest authorized Dexter dealer to Cassopolis - about 45 minutes south through Niles.",
      "Same-day service for boat-trailer bearing repacks - drop Tuesday, launch Saturday on Diamond Lake.",
      "Honest assessment - we'll tell you what's safe for another season and what isn't.",
    ],
    midContent:
      "Diamond Lake is deep, cold, and uses old-school launch ramps in places that submerge trailer hubs deeper than newer launches. That's hard on bearings. Every Cassopolis boat-trailer customer we see should be doing yearly bearing service, and we strongly recommend sealed-hub upgrades for any trailer over 5 years old. The upgrade pays for itself in saved bearings and avoided roadside failures.",
    faqs: [
      {
        q: "Do you service Diamond Lake boat trailers?",
        a: "Yes - Diamond Lake trailers are a regular part of our spring and summer workload. Marine-grade bearing repacks, sealed-hub conversions, and brake service for trailers that get dunked frequently.",
      },
      {
        q: "Where's the closest trailer axle shop to Cassopolis, MI?",
        a: "Axle Inc. in Elkhart, IN - about 45 minutes south. We're the closest authorized Dexter distributor and stock the full line of axles, hubs, and running gear up to 10,000 lb capacity.",
      },
    ],
    neighborhoods: "downtown Cassopolis, Diamond Lake, Stone Lake, and the M-62 corridor",
  },
  {
    slug: "benton-harbor-mi",
    name: "Benton Harbor",
    state: "MI",
    stateName: "Michigan",
    distance: 58,
    direction: "northwest",
    highways: "I-94 and US-31",
    zips: "49022",
    county: "Berrien County",
    intro:
      "Benton Harbor is about 58 miles northwest of Elkhart on I-94 - roughly 75 minutes door to door. Lake Michigan port town with a serious boating economy, marina activity, and a customer base that justifies the drive when service has to be done right.",
    localContext:
      "Benton Harbor and the St. Joseph / Lake Michigan corridor generate some of the heaviest boat-trailer service work we see. Lake Michigan-rated trailers, marina equipment haulers, and the agricultural fruit-belt trailer fleet (apple, peach, and grape haulers) all show up in our shop seasonally.",
    whyChoose: [
      "The most complete trailer parts and Dexter axle inventory within 60 miles of Benton Harbor.",
      "Marine-grade running gear in stock for Lake Michigan boat trailers - bigger trailers, heavier loads, harsher conditions.",
      "Custom axle builds and full replacements available for fatigued or bent boat-trailer axles.",
      "Worth the drive - many Benton Harbor customers batch multiple trailers or service trips for maximum value.",
    ],
    midContent:
      "Lake Michigan boat trailers are a different category from inland-lake trailers. Bigger boats, heavier tongue weights, longer highway runs, and more aggressive corrosion exposure. We stock the heavier-duty Dexter axles (5,200, 6,000, and 7,000 lb capacity) specifically for this market, plus the brake assemblies and hubs to match. Bring a Lake Michigan trailer down and we'll spec it right.",
    faqs: [
      {
        q: "Is the drive from Benton Harbor to Elkhart worth it for trailer service?",
        a: "For Lake Michigan-rated boat trailers and full axle work, yes - our Dexter inventory and expertise on heavier light-duty trailers is difficult to match closer. Many Benton Harbor customers batch multiple trailers per visit.",
      },
      {
        q: "Do you stock heavy-duty axles for Lake Michigan boat trailers?",
        a: "Yes - Dexter axles up to 10,000 lb capacity, with brake and hub configurations to match. Custom builds available for unusual lengths or specifications.",
      },
    ],
    neighborhoods: "downtown Benton Harbor, the St. Joseph waterfront, and the I-94 corridor",
  },
  {
    slug: "kalamazoo-mi",
    name: "Kalamazoo",
    state: "MI",
    stateName: "Michigan",
    distance: 72,
    direction: "north",
    highways: "US-131, I-94, and M-43",
    zips: "49001, 49006, 49007, 49008, 49009, 49048",
    county: "Kalamazoo County",
    intro:
      "Kalamazoo is about 72 miles north of Elkhart on US-131 - roughly 90 minutes door to door. Major regional city, deep manufacturing and academic base, and a customer reach that occasionally brings Kalamazoo trailer owners down to us when local options run out.",
    localContext:
      "Kalamazoo customers we see are typically batching: a fleet manager bringing three or four trailers down at once, a boat-club organizer coordinating spring service for multiple members, or a contractor combining a parts pickup with a downstate job. The drive is real, but the inventory and expertise often justify it.",
    whyChoose: [
      "Authorized Dexter Group distributor with the most complete light-duty axle inventory in the broader region.",
      "Custom axle builds spec'd to your exact requirements - capacity, length, hub face, brake type - built locally.",
      "Volume pricing for Kalamazoo-area dealers, fleets, and manufacturers needing serious trailer parts support.",
      "Honest, knowledgeable shop - sixty-plus combined years of experience available when you call or visit.",
    ],
    midContent:
      "If you're in Kalamazoo and considering the drive, call us first. Many service jobs can be coordinated remotely - send photos of the spec plate, describe the symptoms, and we'll quote and stage parts before you leave. The 90-minute drive becomes a single trip in and out instead of two visits and a wait. That's how most of our Kalamazoo customers work with us.",
    faqs: [
      {
        q: "Is it worth driving from Kalamazoo to Elkhart for trailer service?",
        a: "For custom axle builds, full replacements, or unusual configurations, often yes - our inventory and expertise are hard to match closer. Coordinate by phone first so we can stage parts and quote before the trip.",
      },
      {
        q: "Do you take phone or email orders from Kalamazoo customers?",
        a: "Yes - call or email with photos of your spec plate or old part. We'll identify the components, quote replacements, and have everything staged before you arrive. Most jobs are turn-arounds in a single trip.",
      },
    ],
    neighborhoods: "downtown Kalamazoo, the WMU campus area, Portage, and the US-131 corridor",
  },
];

export const CITY_SLUGS = CITIES.map((c) => c.slug);

export function getCity(slug: string): CityData | undefined {
  return CITIES.find((c) => c.slug === slug);
}

import { CITIES_ES } from "./cities.es.ts";
import type { Locale } from "@/i18n/locale";

/** City data in the requested locale (same slugs, translated copy). */
export function getCities(locale: Locale = "en"): CityData[] {
  return locale === "es" ? CITIES_ES : CITIES;
}
export function getCityL(slug: string, locale: Locale = "en"): CityData | undefined {
  return getCities(locale).find((c) => c.slug === slug);
}
